/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { FormikProps } from 'formik';
import Fuse from 'fuse.js';
import humanize from 'humanize-plus';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Tooltip,
} from '@mui/material';

import { AdvancedSticky } from '../../../components/AdvancedSticky/AdvancedSticky';
import { AlignCenter } from '../../../components/AlignCenter/AlignCenter';
import { BoxPicker } from '../../../components/BoxPicker/BoxPicker';
import { DatasetCard } from '../../../components/DatasetCard/DatasetCard';
import { DataSetCategoriesList } from '../../../components/DataSetCategoriesList/DataSetCategoriesList';
import { CREATE_CLUSTER_OVERLAY_CSS_ANCHOR, DATASET_DEFAULT_READONLY } from '../../../constants/settings';
import { Namespaces } from '../../../i18n';
import { PlanState } from '../../../store/plan/types';
import { useSelectClusterDataSetsStyles } from '../../../styles/selectClusterDataSets';
import { Dataset } from '../../../types/dataset/Dataset';
import { DatasetCategory } from '../../../types/dataset/DatasetCategory';
import { fetchDatasets } from '../../../utils/queryFns';
import { initialImportDatasetMeta } from '../CreateClusterOverlay.consts';
import { CreateClusterValues } from '../CreateClusterOverlay.types';
import { PlansContext } from './forms/consts';

type CreateClusterStepDataSetsProps<Values> = FormikProps<Values> & {
  plan: PlanState;
  buttons: React.ReactElement<any>;
};

const fuseOptions = {
  ignoreLocation: true,
  threshold: 0,
};

export function CreateClusterStepDataSets<Values extends CreateClusterValues>(
  props: CreateClusterStepDataSetsProps<Values>,
) {
  const { values, setFieldValue, plan, handleChange, buttons } = props;
  const { t, i18n } = useTranslation([Namespaces.General, Namespaces.Common]);
  const [query, setQuery] = useState<string>('');
  const classes = useSelectClusterDataSetsStyles();
  const { selectedDataSetCategoryId, selectedDataSetIds, dataSetsMetas } = values;

  const dataSetsCategoriesQuery = useQuery([fetchDatasets.key, { language: i18n.language }], fetchDatasets.fn, {
    refetchOnMount: true,
  });

  const dataSetsCategories = useMemo(
    () => (dataSetsCategoriesQuery.data?.payload ?? []).filter(i => i.datasets.length > 0),
    [dataSetsCategoriesQuery.data],
  );

  const { selectedPlans } = useContext(PlansContext);
  const { storageId } = selectedPlans;

  const { selectedFilteredCategory, selectedCategory, filteredDataSetsCategories } = useMemo<{
    filteredDataSetsCategories: DatasetCategory[];
    selectedFilteredCategory?: DatasetCategory;
    selectedCategory?: DatasetCategory;
  }>(() => {
    const filteredDataSetsCategories = dataSetsCategories.map(c => ({
      ...c,
      datasets: query
        ? new Fuse(c.datasets, {
            ...fuseOptions,
            keys: ['name'],
          })
            .search(query)
            .map(v => v.item)
        : c.datasets,
    }));
    const selectedCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
    const selectedFilteredCategory = filteredDataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
    return {
      filteredDataSetsCategories,
      selectedCategory,
      selectedFilteredCategory,
    };
  }, [dataSetsCategories, query, selectedDataSetCategoryId]);

  const dataSets = useMemo(() => selectedFilteredCategory?.datasets ?? [], [selectedFilteredCategory]);

  const selectedStoragePlan = useMemo(
    () => plan.planStorage?.find(s => s.id === storageId),
    [plan.planStorage, storageId],
  );

  const isLoading = useMemo(() => dataSetsCategoriesQuery.status === 'loading', [dataSetsCategoriesQuery.status]);

  const isError = useMemo<boolean>(() => Boolean(!selectedStoragePlan), [selectedStoragePlan]);

  useEffect(() => {
    if (dataSetsCategories.length) {
      if (!selectedCategory && selectedDataSetCategoryId) {
        const currentDataSetCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
        if (currentDataSetCategory) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        } else {
          void setFieldValue('selectedDataSetCategoryId', undefined);
        }
      } else if (!selectedCategory && !selectedDataSetCategoryId) {
        const currentDataSetCategory = dataSetsCategories[0];
        if (currentDataSetCategory) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        }
      } else {
        const currentDataSetCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
        if (currentDataSetCategory && currentDataSetCategory.id !== selectedCategory?.id) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        }
      }
    }
  }, [dataSetsCategories, selectedCategory, selectedDataSetCategoryId, setFieldValue]);

  const allDataSets = useMemo(() => {
    const result: Dataset[] = [];
    dataSetsCategories.forEach(cat => {
      cat.datasets.forEach(set => {
        result.push(set);
      });
    });
    return result;
  }, [dataSetsCategories]);

  useEffect(() => {
    const nextMetas: CreateClusterValues['dataSetsMetas'] = {
      ...values.dataSetsMetas,
    };
    let shouldUpdate = false;
    allDataSets.forEach(dataSet => {
      if (!dataSetsMetas[dataSet.id]) {
        shouldUpdate = true;
        nextMetas[dataSet.id] = { ...initialImportDatasetMeta };
      }
    });
    if (shouldUpdate) {
      void setFieldValue('dataSetsMetas', nextMetas);
    }
  }, [allDataSets, dataSetsMetas, setFieldValue, values.dataSetsMetas]);

  const selectedDataSets = useMemo(() => {
    const result: Dataset[] = [];
    allDataSets.forEach(set => {
      if (selectedDataSetIds.includes(set.id)) {
        result.push(set);
      }
    });
    return result;
  }, [allDataSets, selectedDataSetIds]);

  const categoriesCounts = useMemo<{
    [key: string]: [number, number];
  }>(() => {
    const result: {
      [key: string]: [number, number];
    } = {};
    dataSetsCategories.forEach(cat => {
      let count = 0;
      cat.datasets.forEach(set => {
        if (selectedDataSetIds.includes(set.id)) {
          count++;
        }
      });
      result[cat.id] = [count, filteredDataSetsCategories.find(v => v.id === cat.id)?.datasets.length ?? 0];
    });
    return result;
  }, [dataSetsCategories, filteredDataSetsCategories, selectedDataSetIds]);

  const {
    availableSizeHumanized,
    accessibleSizeHumanized,
    selectedSetsImportSizeHumanized,
    selectedSetsImportSize,
    availableSize,
    selectedSetsFinalSizeHumanized,
    accessibleSizeMBytes,
  } = useMemo(() => {
    const selectedSetsFinalSize =
      selectedDataSets.reduce((prev, current) => prev + +current.finalSizeMb, 0) * Math.pow(1024, 2);

    const selectedSetsImportSize =
      selectedDataSets.reduce((prev, current) => prev + +current.importSizeMb, 0) * Math.pow(1024, 2);

    const availableSize = (selectedStoragePlan?.size ?? 0) * Math.pow(1024, 3);
    const availableSizeMBytes = availableSize / Math.pow(1024, 2);

    const accessibleSize = Math.max(0, availableSize - selectedSetsImportSize);
    const accessibleSizeMBytes = accessibleSize / Math.pow(1024, 2);

    return {
      selectedSetsFinalSize,
      selectedSetsFinalSizeHumanized: humanize.fileSize(selectedSetsFinalSize),
      selectedSetsImportSize,
      selectedSetsImportSizeHumanized: humanize.fileSize(selectedSetsImportSize),

      accessibleSize,
      accessibleSizeMBytes,
      accessibleSizeHumanized: humanize.fileSize(accessibleSize),
      availableSize,
      availableSizeMBytes,
      availableSizeHumanized: humanize.fileSize(availableSize),
    };
  }, [selectedDataSets, selectedStoragePlan]);

  useEffect(() => {
    if (selectedSetsImportSize > availableSize) {
      void setFieldValue('selectedDataSetIds', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDataSetStatusData = (dataSet: Dataset): { disabled: boolean; disableReason: 'insufficient_space' | '' } => {
    if (+dataSet.importSizeMb > accessibleSizeMBytes && values.selectedDataSetIds.indexOf(dataSet.id) === -1) {
      return {
        disabled: true,
        disableReason: 'insufficient_space',
      };
    }
    return {
      disabled: false,
      disableReason: '',
    };
  };

  return isLoading ? (
    <AlignCenter className={classes.loaderContainer}>
      <CircularProgress color="primary" size={40} />
    </AlignCenter>
  ) : isError ? (
    <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
  ) : (
    <>
      <div className={classes.filtersContainer}>
        <span />
        <div className={classes.filterControlContainer}>
          <div className={cx(classes.filterControlLabel, classes.ml10)}>{t('data_sets.search')}</div>
          <div className={cx(classes.filterControl, classes.ml10)}>
            <FormControl className={classes.w100} variant="outlined">
              <InputLabel htmlFor="dataset_name">{t('data_sets.dataset_name')}</InputLabel>
              <OutlinedInput
                id="dataset_name"
                name="dataset_name"
                label={t('data_sets.dataset_name')}
                fullWidth
                type="text"
                value={query}
                onChange={({ target }) => setQuery(target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="search"
                      onClick={() => setQuery('')}
                      onMouseDown={() => setQuery('')}
                      edge="end"
                      size="large"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </div>
        </div>
      </div>
      <div className={classes.dataSetsStepContainer}>
        <div className={classes.dataSetsCategoriesContainer}>
          <div className={classes.dataSetsCategoriesContainerStickyWrapper}>
            <AdvancedSticky topOffset={70} scrollElement={`.${CREATE_CLUSTER_OVERLAY_CSS_ANCHOR}`} bottomOffset={66}>
              {({ style, elementRef }) => (
                <div style={style} ref={elementRef}>
                  <div className={classes.t}>{t('data_sets.categories_list_title')}</div>
                  <DataSetCategoriesList
                    options={filteredDataSetsCategories}
                    selected={selectedFilteredCategory}
                    onItemClick={(_e, v) => setFieldValue('selectedDataSetCategoryId', v.id)}
                    getOptionLabel={o => o.i18n.title}
                    getOptionThumbnail={o => o.thumbnail}
                    getOptionCount={option => categoriesCounts[option.id]}
                    getOptionSelected={option => option.id === selectedCategory?.id}
                  />
                </div>
              )}
            </AdvancedSticky>
          </div>
          <div className={classes.controlButtons}>{buttons}</div>
        </div>
        <div className={classes.dataSetsGridContainer}>
          <AdvancedSticky scrollElement={`.${CREATE_CLUSTER_OVERLAY_CSS_ANCHOR}`} topOffset={70}>
            {({ elementRef, style }) => (
              <div style={style} ref={elementRef}>
                <div className={classes.t}>{t('data_sets.categories_grid_title')}</div>
                {dataSets.length > 0 ? (
                  <div className={classes.dataSetsGrid}>
                    <BoxPicker
                      selected={selectedDataSetIds}
                      onChange={value => setFieldValue('selectedDataSetIds', value)}
                      multiple
                    >
                      {dataSets.map(dataSet => {
                        const status = getDataSetStatusData(dataSet);
                        return (
                          <Tooltip
                            title={status.disabled ? t(`data_sets.disable_reasons.${status.disableReason}`)! : ''}
                            key={dataSet.id}
                          >
                            <BoxPicker.Item value={dataSet.id} disabled={status.disabled}>
                              <DatasetCard
                                dataSet={dataSet}
                                className={cx(classes.disablableBox, {
                                  [classes.disabled]: status.disabled,
                                })}
                                renderToLower={() => (
                                  <Tooltip
                                    title={status.disabled ? '' : t('data_sets.hints.readonly_meaning')!}
                                    placement="left"
                                  >
                                    <FormControlLabel
                                      className={classes.readonlyCheckboxLabel}
                                      name={`dataSetsMetas.${dataSet.id}.readonly`}
                                      checked={dataSetsMetas[dataSet.id]?.readonly}
                                      defaultChecked={DATASET_DEFAULT_READONLY}
                                      onClick={event => {
                                        event.stopPropagation();
                                      }}
                                      onChange={handleChange}
                                      control={
                                        <Checkbox
                                          color="primary"
                                          size="small"
                                          disableRipple
                                          className={classes.readonlyCheckbox}
                                        />
                                      }
                                      label={t('data_sets.readonly')}
                                      labelPlacement="end"
                                      disabled={status.disabled}
                                    />
                                  </Tooltip>
                                )}
                              />
                            </BoxPicker.Item>
                          </Tooltip>
                        );
                      })}
                    </BoxPicker>
                  </div>
                ) : (
                  <div className={classes.noDataSetsText}>{t('data_sets.no_datasets_in_category')}</div>
                )}
                <div className={classes.divider} />
                <div className={classes.dataSetsSummaryStickyContainer}>
                  <div className={classes.dataSetsSummary}>
                    <span>
                      {t('data_sets.summary.dataset_selection', {
                        count: selectedDataSets.length,
                      })}
                    </span>
                    <span>
                      {t('data_sets.summary.size_info', {
                        total: selectedSetsFinalSizeHumanized,
                        totalImport: selectedSetsImportSizeHumanized,
                        accessible: accessibleSizeHumanized,
                        available: availableSizeHumanized,
                      })}
                    </span>
                  </div>
                  <div className={classes.dataSetsBottomHint}>{t('data_sets.long_import_hint')}</div>
                </div>
              </div>
            )}
          </AdvancedSticky>
        </div>
      </div>
    </>
  );
}
